import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  ANSWERS_ANCHOR,
  IMAGE_SIZES,
  answerCountLine,
  answerFirstLabel,
  answerFirstPoints,
  coverThumbs,
  discussionAuthorHref,
  discussionExcerpt,
  discussionHref,
  discussionKindLabel,
  gearChips,
  ownerAnswersLine,
  ownersNotifiedLine,
  replyButtonLabel,
  teamAnswered,
  teamAskedLine,
} from 'lib/community/discussions'
import { knownTier, sentCount, shortAge, tierLabel } from 'lib/community/model'
import type { DiscussionCard as Card } from 'lib/community/discussions-types'
import type { CommunityPoints } from 'lib/community/types'
import { MaybeLink } from '../Crumbs'
import MemberName from '../MemberName'
import TierAvatar from '../TierAvatar'
import VerifiedCheck from '../VerifiedCheck'
import EditedMarker from './EditedMarker'
import TeamAsked from './TeamAsked'
import TeamPill from './TeamPill'
import styles from './Discussions.module.css'

interface DiscussionCardProps {
  card: Card
  /** The page's timestamp — ages are printed against it so server and client agree. */
  now: number
  /** DealHunt's points table (the summary's): "Answer first" carries what an answer earns.
   *  Without it nothing is promised. */
  points?: Pick<CommunityPoints, 'reply'> | null
  /** On a product page the product is the page: no gear chips, no photos. */
  compact?: boolean
  /** The title's heading level: h2 in a page's list, h3 inside a section. */
  headingLevel?: 'h2' | 'h3'
}

/**
 * One question or discussion in a list (design 10a): the kind tag and the author line, the title
 * (to the thread), a two-line excerpt, up to three photos ("+N" on the third), the linked gear as
 * chips ("+N" after two), and the footer — ▲ DealHunt's helpful count, the answer count and how
 * many came from owners, or, while nobody has answered, "Answer first +10" and how many owners
 * were notified.
 *
 * A staff author (DealHunt's `is_staff`, its allowlist) wears a SOUNDGUYS pill.
 *
 * The SoundGuys team (DealHunt's `staff_answer_count`, `team_mentioned_at` — both optional): once a
 * team profile has answered, "[SOUNDGUYS TEAM] answered"; while the team was asked and has not
 * answered, "(bell) Team asked · 40m ago" — whatever the answer count. Absent fields draw nothing.
 *
 * On a phone, in the Discussions list only (Community mobile M2, CSS under `.list`): the title is
 * 17px with a two-line clamp (display only — the stored title is whole), and an unanswered card's
 * footer reads "No replies yet" with a 44px "Reply · +10" outline button (both labels in the HTML,
 * one shown). The product page's list keeps its layout.
 *
 * Shared-cached HTML: nothing here is the reader's. The ▲ count is DealHunt's public figure (no
 * control on a card — the vote is the thread's); every figure renders only when DealHunt sends it.
 */
const DiscussionCard: React.VFC<DiscussionCardProps> = ({
  card,
  now,
  points = null,
  compact = false,
  headingLevel: Heading = 'h2',
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const href = discussionHref(card, trailingSlash)
  const answersHref = `${href}#${ANSWERS_ANCHOR}`
  const authorHref = discussionAuthorHref(card.author, trailingSlash)
  const tier = knownTier(card.author.tier)
  const age = shortAge(card.published_at, now)
  const excerpt = discussionExcerpt(card)
  const thumbs = compact ? [] : coverThumbs(card)
  const gear = compact ? { chips: [], more: 0 } : gearChips(card.gear, trailingSlash)
  const helpful = sentCount(card.helpful_count)
  const answers = sentCount(card.answer_count)
  const fromOwners = ownerAnswersLine(card.owner_answer_count)
  const notified = ownersNotifiedLine(card.owners_notified)
  const pts = answerFirstPoints(points)
  const byTeam = teamAnswered(card)
  const asked = teamAskedLine(card, now)

  const authorName = (
    <MemberName
      name={card.author.display_name}
      handle={card.author.handle}
      handleDisplay={card.author.handle_display}
    />
  )

  return (
    <article className={styles.card} data-discussion-id={card.id}>
      <p className={styles.meta}>
        <span className={clsx(styles.kind, { [styles.kindDiscussion]: card.kind !== 'question' })}>
          {discussionKindLabel(card.kind)}
        </span>
        <TierAvatar
          handle={card.author.handle}
          tier={tier}
          size="xs"
          src={card.author.avatar_url}
          name={card.author.display_name}
        />
        {authorHref ? (
          <Linked href={authorHref} className={styles.author}>
            {authorName}
            <VerifiedCheck />
          </Linked>
        ) : (
          <span className={styles.authorPlain}>{authorName}</span>
        )}
        {card.author.is_staff === true && <span className={styles.staff}>{'SoundGuys'}</span>}
        {card.author.owns_linked ? (
          <span className={styles.owner}>
            <span className={styles.check} aria-hidden="true">
              {'✓'}
            </span>
            {'Owner'}
          </span>
        ) : (
          <span>{`· ${tierLabel(tier)}`}</span>
        )}
        {age && <time dateTime={card.published_at}>{`· ${age}`}</time>}
        <EditedMarker at={card.edited_at} />
      </p>
      <Heading className={styles.title}>
        <Linked href={href} className={styles.titleLink}>
          {card.title}
        </Linked>
      </Heading>
      {excerpt && <p className={styles.excerpt}>{excerpt}</p>}
      {thumbs.length > 0 && (
        <div className={styles.thumbs}>
          {thumbs.map(thumb => (
            <Linked key={thumb.src} href={href} className={styles.thumb}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={thumb.src}
                srcSet={thumb.srcSet}
                sizes={thumb.srcSet ? IMAGE_SIZES.cardThumb : undefined}
                alt=""
                loading="lazy"
                decoding="async"
                width={thumb.width}
                height={thumb.height}
              />
              {thumb.more > 0 && <span className={styles.thumbMore}>{`+${thumb.more}`}</span>}
            </Linked>
          ))}
        </div>
      )}
      {gear.chips.length > 0 && (
        <ul className={styles.chips} aria-label="Linked gear">
          {gear.chips.map(chip => {
            if (chip.kind === 'brand') {
              return (
                <li key={chip.key} className={styles.chip}>
                  {chip.name}
                  <span className={styles.chipBrand}>{'brand'}</span>
                </li>
              )
            }
            return (
              <li key={chip.key}>
                <MaybeLink href={chip.href} className={clsx(styles.chip, styles.chipProduct)}>
                  <span className={styles.chipThumb}>
                    {chip.image && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={chip.image}
                        alt=""
                        width={20}
                        height={20}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </span>
                  {chip.name}
                </MaybeLink>
              </li>
            )
          })}
          {gear.more > 0 && (
            <li className={clsx(styles.chip, styles.chipMore)}>{`+${gear.more}`}</li>
          )}
        </ul>
      )}
      <div className={styles.foot}>
        {helpful !== null && (
          <span
            aria-label={
              card.kind === 'question' ? `${helpful} with the same question` : `${helpful} helpful`
            }
          >
            {`▲ ${helpful.toLocaleString('en-US')}`}
          </span>
        )}
        {answers !== null && answers > 0 && (
          <Linked href={answersHref} className={styles.footLink}>
            {answerCountLine(card.kind, answers)}
          </Linked>
        )}
        {byTeam && (
          <span className={styles.teamAnswered}>
            <TeamPill />
            {'answered'}
          </span>
        )}
        {answers !== null && answers > 0 && fromOwners && (
          <span className={styles.fromOwners}>
            <span className={styles.fromOwnersCheck} aria-hidden="true">
              {'✓'}
            </span>
            {fromOwners}
          </span>
        )}
        {answers === 0 && (
          <span className={clsx(styles.phoneLabel, styles.noReplies)}>{'No replies yet'}</span>
        )}
        {asked && <TeamAsked line={asked} />}
        {answers === 0 && (
          <Linked href={answersHref} className={styles.answerFirst}>
            <span className={styles.wideLabel}>
              {answerFirstLabel(card.kind)}
              {pts && <span className={styles.pts}>{pts}</span>}
            </span>
            <span className={styles.phoneLabel}>{replyButtonLabel(points)}</span>
          </Linked>
        )}
        {answers === 0 && notified && <span className={styles.notified}>{notified}</span>}
      </div>
    </article>
  )
}

export default DiscussionCard
